import MeridianColumn from '@amzn/meridian/column';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import styled from '@emotion/styled';
import _ from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RETURN_REASON } from '../../constants';
import { useItemTableHelpers } from '../../hooks/useItemTableHelpers';
import { fetchAttachmentsByReturnId } from '../../redux/actions/attachmentsActions';
import { getUnmappedItems } from '../../redux/actions/resellerActionCreators';
import {
    IAsynchronouslyLoadedValue,
    ReturnOrderLineItemStatus,
    returnOrderLineItemStatuses,
    ReturnOrderLineItem,
    ComplianceReview,
    ResellerItem,
    RootState,
    ReturnOrderType,
} from '../../redux/types';
import { color } from '../../theme/colors';
import { PackageType } from '../../types';
import { Debug } from '../../utils/debug';
import { isNormalizedReturnReason } from '../../utils/returnReasons';
import { Button, TranslatedString, useTranslation } from '../blocks';
import { HelpPopover } from '../constructed';
import { ColumnFormat, SuperPaginatedTable, SuperPaginatedTableColumn } from '../constructed/SuperPaginatedTable';
import { externalLinkFormat, numberFormat, viewImageFormat } from '../constructed/SuperPaginatedTable.formats';
import { ColumnFilter } from './ColumnFilter';

const BorderedColumn = styled(MeridianColumn)`
    border: 1px solid ${color.gray[300]};
    box-shadow: 0px 0px 3px ${color.gray[300]};
    th button:focus {
        outline: thin dotted;
    }
`;

export type ReturnOrderItemListProps = {
    resellerId: string;
    returnOrderId: string;
    items: IAsynchronouslyLoadedValue<ReturnOrderLineItem[]>;
    resellerItems: ResellerItem[];
    returnType: ReturnOrderType;
};

// eslint-disable-next-line max-lines-per-function
export const ReturnOrderItemList: FC<ReturnOrderItemListReduxProps & ReturnOrderItemListProps> = ({
    resellerId,
    returnOrderId,
    items,
    resellerItems,
    returnType,
    asinToImageUrls,
    attachments,
    fetchAttachmentsByReturnId,
    unmappedItemStatus,
    getUnmappedItems,
}) => {
    const { t } = useTranslation(['returnOrderItemList', 'uploadImageModal']);

    const [itemFilterValue, setItemFilterValue] = useState<string | undefined>(undefined);
    const [statusFilterValue, setStatusFilterValue] = useState<string | undefined>(undefined);
    const [areAttachmentsCleared, setAreAttachmentsCleared] = useState<boolean>(false);
    const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});
    const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    useEffect(() => {
        if (attachments.status === 'Uninitialized' || !areAttachmentsCleared) {
            setAreAttachmentsCleared(true);
            fetchAttachmentsByReturnId({ returnOrderId });
        }
    }, [attachments, fetchAttachmentsByReturnId, returnOrderId, areAttachmentsCleared]);

    const itemMap = useMemo(() => new Map(resellerItems.map((item) => [item.itemId, item])), [resellerItems]);

    useEffect(() => {
        if (resellerId) {
            const unmappedItemIds =
                items.value && items.value.map((item) => item.itemId).filter((itemId) => !itemMap.has(itemId));
            if (unmappedItemIds && unmappedItemIds.length > 0 && unmappedItemStatus === 'Uninitialized') {
                getUnmappedItems(resellerId, unmappedItemIds);
            }
        }
    }, [resellerId, unmappedItemStatus, itemMap, items, resellerItems, getUnmappedItems]);

    const { titleFormat, imageFormat, skuFormat, upcFormat, asinFormat, getSku } = useItemTableHelpers(
        itemMap,
        asinToImageUrls,
        'returnOrderItemList'
    );

    const getRawSku = useCallback(
        (itemId: string): string | undefined => {
            return itemMap.get(itemId)?.externalSku;
        },
        [itemMap]
    );

    const allItems = useMemo(
        () =>
            _.chain(items.value ?? [])
                .map((item) => {
                    return getSku(item.itemId);
                })
                .uniq()
                .value(),
        [getSku, items.value]
    );

    const itemStatusFormat: ColumnFormat = useMemo(() => {
        const getText = (status: ReturnOrderLineItemStatus) => t('itemStatus-value', { context: status });

        return {
            render: (status: ReturnOrderLineItemStatus, item: ReturnOrderLineItem) => {
                switch (status) {
                    case 'ON_CREDIT_MEMO': {
                        // TODO: use a real URL.
                        const externalLinkRenderFunction = externalLinkFormat(
                            (status: ReturnOrderLineItemStatus, item: ReturnOrderLineItem) =>
                                `http://kor-portal.amazon.com/credit-memos/${item.creditMemoNumber}`,
                            { render: getText }
                        ).render;
                        Debug.assertExists(externalLinkRenderFunction);
                        return externalLinkRenderFunction(status, item);
                    }
                    default:
                        return getText(status);
                }
            },
            sort: getText,
            search: getText,
        };
    }, [t]);

    const complianceFormat = (getText: (complianceReview: ComplianceReview) => TranslatedString) => ({
        render: getText,
    });

    const complianceEligibleQuantityFormat: ColumnFormat = useMemo(
        () =>
            complianceFormat((complianceReview: ComplianceReview) =>
                complianceReview.eligibleReturnQuantity
                    ? t.cognate(`${complianceReview.eligibleReturnQuantity}`)
                    : t('eligibleQuantity-value_notApplicable')
            ),
        [t]
    );

    const returnReasonFormat: ColumnFormat = useMemo(() => {
        const getReturnReasonText = (returnReason: string) => {
            if (!returnReason) return '';
            if (isNormalizedReturnReason(returnReason)) {
                return t('orderItemReason', { context: returnReason.toUpperCase() });
            }
            return t.cognate(returnReason);
        };

        const getSearchText = (_: string, item: ReturnOrderLineItem) => {
            if (!item?.consolidatedReturnReasons?.returnReasonQuantities) return '';
            return Object.keys(item.consolidatedReturnReasons.returnReasonQuantities)
                .map(getReturnReasonText)
                .join(' ');
        };

        const renderSingleReason = (reason: string) => {
            if (reason.toUpperCase() === RETURN_REASON.NONE) {
                return <Text>{t.cognate('')}</Text>;
            }
            return <Text>{getReturnReasonText(reason)}</Text>;
        };

        const renderMultipleReasons = (item: ReturnOrderLineItem, reasons: [string, number][]) => {
            const popoverId = `consolidated-reasons-${item.lineItemId}`;
            const isOpen = openPopovers[popoverId] || false;

            return (
                <>
                    <Button
                        ref={(el) => {
                            buttonRefs.current[popoverId] = el;
                        }}
                        onClick={() => setOpenPopovers((prev) => ({ ...prev, [popoverId]: true }))}
                        type={'tertiary'}
                    >
                        {t('orderItemReason_multiple_reasons-placeholder')}
                    </Button>
                    {buttonRefs.current[popoverId] && (
                        <Popover
                            anchorNode={buttonRefs.current[popoverId]!}
                            open={isOpen}
                            onClose={() => setOpenPopovers((prev) => ({ ...prev, [popoverId]: false }))}
                            position={'end'}
                        >
                            <Table headerRows={1} showDividers={true}>
                                <TableRow>
                                    <TableCell>{t('returnReason-columnHeader')}</TableCell>
                                    <TableCell>{t('quantity-columnHeader')}</TableCell>
                                </TableRow>
                                {reasons.map(([reason, quantity]) => (
                                    <TableRow key={reason}>
                                        <TableCell>{getReturnReasonText(reason)}</TableCell>
                                        <TableCell>{quantity}</TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        </Popover>
                    )}
                </>
            );
        };

        const renderReasons = (item: ReturnOrderLineItem) => {
            if (!item?.consolidatedReturnReasons?.returnReasonQuantities) return null;

            const reasons = Object.entries(item.consolidatedReturnReasons.returnReasonQuantities);
            const totalQuantity = Object.values(item.consolidatedReturnReasons.returnReasonQuantities).reduce(
                (sum, qty) => sum + qty,
                0
            );

            const isSingleReasonForAllItems = reasons.length === 1 && totalQuantity === item.requestedCount;

            return isSingleReasonForAllItems ? renderSingleReason(reasons[0][0]) : renderMultipleReasons(item, reasons);
        };

        const renderComplianceInfo = (item: ReturnOrderLineItem) => {
            if (item.complianceResolution !== 'DISPOSE') return null;

            return (
                <>
                    <Text>{t('complianceResolution_DISPOSE')}</Text>
                    {item.itemId && getRawSku(item.itemId)?.startsWith('NO SKU') && (
                        <HelpPopover
                            text={t('noRefund_for_NOSKU_item')}
                            position={'right'}
                            maxWidth={'60%'}
                            dataTestId={`noRefundPop-${item.itemId}`}
                        />
                    )}
                </>
            );
        };

        const getRenderedText = (_: string, item: ReturnOrderLineItem) => {
            if (!item) return <></>;

            return (
                <Row>
                    {renderReasons(item)}
                    {renderComplianceInfo(item)}
                </Row>
            );
        };

        const getSort = (_: string, item: ReturnOrderLineItem) =>
            item?.consolidatedReturnReasons?.returnReasonQuantities ? '0' : '1';

        return {
            render: getRenderedText,
            sort: getSort,
            search: getSearchText,
        };
    }, [t, getRawSku, openPopovers]);

    const denialReasonFormat: ColumnFormat = useMemo(() => {
        const getText = (_: string, item: ReturnOrderLineItem) => {
            return item?.denialReason?.toString() || '';
        };

        const getSearchText = (_: string, item: ReturnOrderLineItem) => {
            if (!item) return '';

            const denialReasonText = item.denialReason ? `${t('orderItemReason', { context: item.denialReason })}` : '';

            return `${denialReasonText}`.trim();
        };

        const getRenderedText = (_: string, item: ReturnOrderLineItem) => {
            if (!item) return <></>;

            return (
                <Row>{item.denialReason && <Text>{t('orderItemReason', { context: item.denialReason })}</Text>}</Row>
            );
        };

        return {
            render: getRenderedText,
            sort: getText,
            search: getSearchText,
        };
    }, [t]);

    const packageTypeFormat: ColumnFormat = useMemo(() => {
        const getText = (packageType?: PackageType) => {
            if (packageType) {
                return t('packageType', { context: packageType });
            } else {
                return t('packageType-value_missing');
            }
        };

        return {
            render: getText,
        };
    }, [t]);

    const packageQuantityFormat: ColumnFormat = {
        render: (packageQuantity: number) => {
            if (packageQuantity > 0) {
                return packageQuantity.toString();
            } else {
                return t('packageQuantity-value_missing');
            }
        },
    };

    const baseColumns: SuperPaginatedTableColumn[] = [
        {
            sourceProperty: 'lineItemId',
            id: 'lineItemId',
            title: t.cognate('lineItemId'),
            isKey: true,
            isVisible: false,
        },
        {
            sourceProperty: 'itemId',
            id: 'sku',
            title: t('sku-columnHeader'),
            defaultSort: 'ascending',
            format: skuFormat,
            searchable: true,
        },
        {
            sourceProperty: 'itemId',
            id: 'upc',
            title: t('upc-columnHeader'),
            defaultSort: 'ascending',
            format: upcFormat,
            searchable: true,
        },
        {
            sourceProperty: 'itemId',
            id: 'asin',
            title: t('asin-columnHeader'),
            format: asinFormat,
            searchable: true,
        },
        {
            sourceProperty: 'itemId',
            id: 'Title',
            title: t('item-columnHeader'),
            format: titleFormat,
            searchable: true,
        },
        {
            sourceProperty: 'itemId',
            id: 'image',
            title: t.cognate(' '),
            format: imageFormat,
            searchable: true,
        },
    ];

    const standardColumns: SuperPaginatedTableColumn[] = baseColumns.concat([
        {
            sourceProperty: 'requestedCount',
            title: t('requested-columnHeader'),
            format: numberFormat,
            searchable: false,
        },
        {
            sourceProperty: 'approvedCount',
            title: t('approved-columnHeader'),
            format: numberFormat,
            searchable: false,
        },
        {
            sourceProperty: 'receivedCount',
            title: t('received-columnHeader'),
            format: numberFormat,
            searchable: false,
        },
        {
            sourceProperty: 'status',
            title: t('status-columnHeader'),
            format: itemStatusFormat,
            searchable: true,
        },
        {
            sourceProperty: 'returnReason',
            title: t('returnReason-columnHeader'),
            format: returnReasonFormat,
            searchable: true,
        },
        {
            sourceProperty: 'denialReason',
            title: t('denialReason-columnHeader'),
            format: denialReasonFormat,
            searchable: true,
        },
        {
            sourceProperty: 'complianceReview',
            title: t('eligibleQuantity-columnHeader'),
            format: complianceEligibleQuantityFormat,
            searchable: true,
        },
    ]);

    const buybackRefusalColumns: SuperPaginatedTableColumn[] = baseColumns.concat([
        {
            sourceProperty: 'packageType',
            title: t('packageType-columnHeader'),
            format: packageTypeFormat,
            searchable: true,
        },
        {
            sourceProperty: 'packageQuantity',
            title: t('packageQuantity-columnHeader'),
            format: packageQuantityFormat,
            searchable: false,
        },
        {
            sourceProperty: 'requestedCount',
            title: t('requested-columnHeader'),
            format: numberFormat,
            searchable: false,
        },
        {
            sourceProperty: 'approvedCount',
            title: t('approved-columnHeader'),
            format: numberFormat,
            searchable: false,
        },
        {
            sourceProperty: 'receivedCount',
            title: t('received-columnHeader'),
            format: numberFormat,
            searchable: false,
        },
        {
            sourceProperty: 'status',
            title: t('status-columnHeader'),
            format: itemStatusFormat,
            searchable: true,
        },
        {
            sourceProperty: 'lineItemId',
            title: t('uploadImageModal:uploadedImages-columnHeader'),
            format: viewImageFormat(attachments),
            searchable: false,
        },
    ]);

    const allStatuses = useMemo(
        () => _.map(returnOrderLineItemStatuses, (status) => t('itemStatus-value', { context: status })),
        [t]
    );

    return (
        <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
            <SuperPaginatedTable
                search={[
                    { columns: ['sku'], value: itemFilterValue, matchWholeString: true },
                    { columns: ['status'], value: statusFilterValue, matchWholeString: true },
                ]}
                data={items.status === 'Loaded' ? _.uniqBy(items.value ?? [], 'lineItemId') : []}
                layout={{
                    pageSize: 10,
                    searchPlaceholder: t('findResources-searchBoxPlaceholder'),
                    columns: returnType === 'RETURN' ? standardColumns : buybackRefusalColumns,
                }}
            >
                <ColumnFilter
                    label={t('itemFilter-placeholder')}
                    values={allItems}
                    value={itemFilterValue}
                    onChange={setItemFilterValue}
                />
                <ColumnFilter
                    label={t('statusFilter-placeholder')}
                    values={allStatuses}
                    value={statusFilterValue}
                    onChange={setStatusFilterValue}
                />
            </SuperPaginatedTable>
        </BorderedColumn>
    );
};

const mapStateToProps = ({ resellerReducer, attachmentsReducer }: RootState) => {
    return {
        asinToImageUrls: resellerReducer.asinToImageUrls,
        attachments: attachmentsReducer,
        unmappedItemStatus: resellerReducer.unmappedItemStatus,
    };
};

const mapDispatchToProps = {
    fetchAttachmentsByReturnId,
    getUnmappedItems,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type ReturnOrderItemListReduxProps = ConnectedProps<typeof connector>;
export default connector(ReturnOrderItemList);
